import React, { useEffect, useState } from "react";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Autocomplete, Switch, FormControlLabel, IconButton, Typography, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Snackbar, Alert, Tooltip,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import {
  getCalendarCerboMappings, saveOrEditCalendarCerboMapping, deleteCalendarCerboMapping,
  getGhlCalendars, GetProviderList, GetAppointmentTypeEntities,
} from "../../Service/AxiosService";

interface Mapping {
  id?: string;
  ghlCalendarId?: string;
  ghlCalendarName?: string;
  cerboProviderId?: string;
  cerboProviderName?: string;
  cerboAppointmentType?: string;
  cerboLocation?: string;
  enabled: boolean;
}

const emptyForm: Mapping = { enabled: true };

const CalendarCerboSync: React.FC = () => {
  const [rows, setRows] = useState<Mapping[]>([]);
  const [calendars, setCalendars] = useState<{ id: string; name: string }[]>([]);
  const [providers, setProviders] = useState<any[]>([]);
  const [apptTypes, setApptTypes] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Mapping>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: "success" | "error" } | null>(null);

  const loadRows = async () => {
    const res = await getCalendarCerboMappings();
    setRows(res.success && Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    (async () => {
      await loadRows();
      const cal = await getGhlCalendars();
      setCalendars(cal.success && Array.isArray(cal.data) ? cal.data : []);
      const prov = await GetProviderList();
      setProviders(prov.success && Array.isArray(prov.data) ? prov.data : []);
      const types = await GetAppointmentTypeEntities();
      setApptTypes(types.success && Array.isArray(types.data) ? types.data : []);
    })();
  }, []);

  const openAdd = () => { setForm(emptyForm); setOpen(true); };
  const openEdit = (m: Mapping) => { setForm({ ...m }); setOpen(true); };

  const providerLabel = (p: any) => `${p.firstName || ""} ${p.lastName || ""}`.trim() || p.providerId;

  const isValid = !!form.ghlCalendarId && !!form.cerboProviderId && !!form.cerboAppointmentType;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    const res = await saveOrEditCalendarCerboMapping(form);
    setSaving(false);
    if (res.success) {
      setToast({ msg: res.data || "Saved", sev: "success" });
      setOpen(false);
      await loadRows();
    } else {
      setToast({ msg: res.error || "Failed to save", sev: "error" });
    }
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    if (!window.confirm("Delete this calendar → Cerbo rule?")) return;
    const res = await deleteCalendarCerboMapping(id);
    if (res.success) { setToast({ msg: "Deleted", sev: "success" }); await loadRows(); }
    else setToast({ msg: res.error || "Failed to delete", sev: "error" });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Calendar → Cerbo Sync</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            When an appointment is booked on a HighLevel calendar, create a matching appointment in
            Cerbo for the chosen provider — blocking their schedule.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd} sx={{ textTransform: "none" }}>
          New Rule
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>HighLevel Calendar</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cerbo Provider</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cerbo Appointment Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Enabled</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 3 }}>
                No rules yet — click “New Rule” to sync a HighLevel calendar’s bookings into Cerbo.
              </TableCell></TableRow>
            )}
            {rows.map((m) => (
              <TableRow key={m.id} hover>
                <TableCell>{m.ghlCalendarName || m.ghlCalendarId}</TableCell>
                <TableCell>{m.cerboProviderName || m.cerboProviderId}</TableCell>
                <TableCell>{m.cerboAppointmentType}</TableCell>
                <TableCell>
                  <Chip size="small" label={m.enabled ? "Yes" : "No"} color={m.enabled ? "success" : "default"} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(m)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(m.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Calendar → Cerbo Rule</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2, maxHeight: "80vh", overflowY: "auto", minHeight: 0 }}>
          <Autocomplete
            options={calendars}
            getOptionLabel={(o) => o.name || o.id}
            value={calendars.find((c) => c.id === form.ghlCalendarId) || null}
            onChange={(_, v) => setForm({ ...form, ghlCalendarId: v?.id, ghlCalendarName: v?.name })}
            renderInput={(p) => <TextField {...p} label="HighLevel Calendar" size="small" />}
          />
          <Autocomplete
            options={providers}
            getOptionLabel={providerLabel}
            value={providers.find((p) => String(p.providerId) === String(form.cerboProviderId)) || null}
            onChange={(_, v) => setForm({ ...form, cerboProviderId: v?.providerId, cerboProviderName: v ? providerLabel(v) : undefined })}
            renderInput={(p) => <TextField {...p} label="Cerbo Provider (schedule to block)" size="small" />}
          />
          <Autocomplete
            options={apptTypes}
            getOptionLabel={(o: any) => o.appointmentTypeName || ""}
            value={apptTypes.find((t: any) => t.appointmentTypeName === form.cerboAppointmentType) || null}
            onChange={(_, v: any) => setForm({ ...form, cerboAppointmentType: v?.appointmentTypeName })}
            renderInput={(p) => <TextField {...p} label="Cerbo Appointment Type" size="small" />}
          />
          <TextField
            label="Cerbo Location (optional)"
            size="small"
            value={form.cerboLocation || ""}
            onChange={(e) => setForm({ ...form, cerboLocation: e.target.value })}
          />
          <FormControlLabel
            control={<Switch checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />}
            label="Enabled"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" disabled={!isValid || saving} onClick={handleSave} sx={{ textTransform: "none" }}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default CalendarCerboSync;
